import React, { useMemo } from 'react'
import ReactEChartsCore from './EChartsCore.js'
import * as echarts from 'echarts/core'
import type { ComposeOption } from 'echarts/core'
import { TreemapChart, type TreemapSeriesOption } from 'echarts/charts'
import { TooltipComponent, type TooltipComponentOption } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { visTickFormatter } from './Options/vizFormatters.js'
import { CHART_SURFACE, fillStyleFor, identityFor } from './Options/visualIdentity.js'

echarts.use([TreemapChart, TooltipComponent, CanvasRenderer])

type EChartsOption = ComposeOption<TreemapSeriesOption | TooltipComponentOption>

export interface TreemapNode {
  name: string
  value: number
}

interface Props {
  data: TreemapNode[]
  onNodeClick?: (name: string) => void
  height?: number | `${number}%`
}

/**
 * ECharts treemap. One level of tiles, each wearing its name's visual identity; clicking a tile
 * reports its `name`.
 */
export const TreemapChartNext: React.FC<Props> = ({ data, onNodeClick, height }) => {
  const option: EChartsOption = useMemo(() => {
    const total = Math.max(
      1,
      data.reduce((sum, x) => sum + x.value, 0)
    )
    return {
      backgroundColor: CHART_SURFACE,
      animation: false,
      tooltip: {
        trigger: 'item',
        confine: true,
        valueFormatter: (v) => (typeof v === 'number' ? (visTickFormatter(v) ?? String(v)) : String(v ?? ''))
      },
      series: [
        {
          type: 'treemap',
          roam: false,
          nodeClick: false, // we handle clicks ourselves (drill-down via redux), not echarts zoom
          breadcrumb: { show: false },
          label: { show: true, formatter: '{b}', overflow: 'truncate' },
          data: data.map((n) => ({ name: n.name, value: n.value, itemStyle: fillStyleFor(identityFor(n.name), n.value / total) }))
        }
      ]
    }
  }, [data])

  const onEvents = useMemo(
    () => ({
      click: (p: { name?: string }) => {
        if (onNodeClick && typeof p.name === 'string') onNodeClick(p.name)
      }
    }),
    [onNodeClick]
  )

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={option}
      onEvents={onEvents}
      notMerge
      lazyUpdate
      style={{ width: '100%', height: height ?? 400, backgroundColor: CHART_SURFACE }}
    />
  )
}
